import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { usePage, router } from '@inertiajs/react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { SlidersHorizontal, Square, Loader2, AlertTriangle } from 'lucide-react'
import { isAdmin as checkIsAdmin } from '@/lib/auth'
import type { Project } from '@/types/conversations'

const AUTO_STOP_OPTIONS = [
  { value: '60', label: '1h' },
  { value: '180', label: '3h' },
  { value: '300', label: '5h' },
  { value: '1440', label: '24h' },
  { value: '4320', label: '72h' },
  { value: '0', label: 'Always on' },
] as const

interface SandboxSettingsPopoverProps {
  project: Project
  organization?: {
    id: number
    name: string
    credits_remaining: number
    current_plan: string
  }
  isBusy?: boolean
}

export function SandboxSettingsPopover({ project, organization, isBusy = false }: SandboxSettingsPopoverProps) {
  const { t } = useTranslation(['conversations', 'common'])
  const { props } = usePage<{
    auth: {
      user: {
        organization_role: string
        super_admin?: boolean
      }
    }
  }>()

  const isAdmin = checkIsAdmin(props.auth?.user)
  const isFreePlan = organization?.current_plan === 'free'
  const isRunning = project.runtime_state === 'running'
  const isSandboxActive = isRunning && project.instance_status === 'active'
  const isManagedAgentProject = project.project_type === 'managed_agent'

  const [isStopping, setIsStopping] = useState(false)
  const [showStopDialog, setShowStopDialog] = useState(false)
  const [isResizing, setIsResizing] = useState(false)
  const [selectedSize, setSelectedSize] = useState(project.instance_size || 'S')
  const [isUpdatingTimeout, setIsUpdatingTimeout] = useState(false)

  useEffect(() => {
    setSelectedSize(project.instance_size || 'S')
  }, [project.instance_size])

  const sizes = project.instance_sizes || []
  const maxAllowedSize = project.instance_max_allowed_size || 'S'
  const maxIndex = sizes.findIndex(s => s.value === maxAllowedSize)
  const canResize = isSandboxActive && !isFreePlan && selectedSize !== project.instance_size
  const hourlyCost = sizes.find(s => s.value === selectedSize)!.hourly_cost

  const handleResize = () => {
    if (!canResize) return
    setIsResizing(true)
    router.post(`/projects/${project.id}/resize_instance`, {
      size: selectedSize
    }, {
      preserveScroll: true,
      onFinish: () => setIsResizing(false)
    })
  }

  const handleStopInstance = () => {
    setIsStopping(true)
    setShowStopDialog(false)
    router.post(`/projects/${project.id}/stop_instance`, {}, {
      preserveScroll: true,
      onError: () => setIsStopping(false),
      onFinish: () => setIsStopping(false)
    })
  }

  const handleAutoStopChange = (value: string) => {
    setIsUpdatingTimeout(true)
    router.post(`/projects/${project.id}/update_auto_stop_timeout`, {
      timeout: value
    }, {
      preserveScroll: true,
      onFinish: () => setIsUpdatingTimeout(false)
    })
  }

  return (
    <>
      <Popover>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground hover:bg-accent"
              >
                <SlidersHorizontal className="h-3.5 w-3.5" />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>
            {isManagedAgentProject ? t('preview.managedAgent.workspaceSettings') : t('preview.sandboxSettings.tooltip')}
          </TooltipContent>
        </Tooltip>
        <PopoverContent align="end" className="w-72 p-0">
          <div className="px-3 py-2.5">
            <p className="text-sm font-medium">
              {isManagedAgentProject ? t('preview.managedAgent.workspaceSettingsTitle') : t('preview.sandboxSettings.title')}
            </p>
          </div>
          <Separator />

          {/* Sandbox Size */}
          <div className="px-3 py-2.5 space-y-1.5">
            <p className="text-xs font-medium text-muted-foreground">
              {isManagedAgentProject ? t('preview.managedAgent.workspaceSize') : t('preview.sandboxSettings.size')}
            </p>
            {!isFreePlan ? (
              <div className="flex items-center gap-2">
                <Select
                  value={selectedSize}
                  onValueChange={setSelectedSize}
                  disabled={!isSandboxActive}
                >
                  <SelectTrigger className="h-8 text-xs flex-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {sizes.map((sizeOption, index) => {
                      const isAllowed = index <= maxIndex
                      return (
                        <SelectItem
                          key={sizeOption.value}
                          value={sizeOption.value}
                          disabled={!isAllowed}
                        >
                          {sizeOption.value} — {sizeOption.memory_gb} GB, {sizeOption.max_workers === 0 ? `1 ${t('preview.sandboxSettings.worker')}` : `${sizeOption.max_workers} ${t('preview.sandboxSettings.workers')}`}
                          {!isAllowed && ` (${t('preview.sandboxSettings.requiresUpgrade')})`}
                        </SelectItem>
                      )
                    })}
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  className="h-8 text-xs"
                  onClick={handleResize}
                  disabled={!canResize || isResizing}
                >
                  {isResizing ? <Loader2 className="h-3 w-3 animate-spin" /> : t('preview.sandboxSettings.resize')}
                </Button>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">{t('preview.sandboxSettings.sizeRequiresPaid')}</p>
            )}
          </div>
          <Separator />

          {/* Auto-Stop Timeout */}
          <div className="px-3 py-2.5 space-y-1.5">
            <p className="text-xs font-medium text-muted-foreground">{t('preview.sandboxSettings.autoStop')}</p>
            <Select
              value={String(project.auto_stop_timeout)}
              onValueChange={handleAutoStopChange}
              disabled={!isAdmin || isUpdatingTimeout}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AUTO_STOP_OPTIONS.map(option => {
                  const isAlwaysOn = option.value === '0'
                  const disabled = isAlwaysOn && isFreePlan
                  return (
                    <SelectItem
                      key={option.value}
                      value={option.value}
                      disabled={disabled}
                    >
                      {option.label}
                      {disabled && ` (${t('preview.sandboxSettings.requiresUpgrade')})`}
                    </SelectItem>
                  )
                })}
              </SelectContent>
            </Select>
            {!isAdmin && (
              <p className="text-xs text-muted-foreground">{t('preview.sandboxSettings.adminOnly')}</p>
            )}
          </div>
          <Separator />

          {/* Cost Display */}
          <div className="px-3 py-2.5">
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">{t('preview.sandboxSettings.hourlyCost')}</p>
              <p className="text-xs font-medium">{hourlyCost} {t('preview.sandboxSettings.creditsPerHour')}</p>
            </div>
          </div>
          <Separator />

          {/* Stop Button */}
          <div className="px-3 py-2.5">
            <Button
              size="sm"
              variant="destructive"
              className="w-full h-8 text-xs"
              onClick={() => setShowStopDialog(true)}
              disabled={!isRunning || isStopping}
            >
              {isStopping ? (
                <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
              ) : (
                <Square className="mr-1.5 h-3 w-3" />
              )}
              {isManagedAgentProject ? t('preview.managedAgent.stopButton') : t('preview.sandboxSettings.stopSandbox')}
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      <AlertDialog open={showStopDialog} onOpenChange={setShowStopDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isManagedAgentProject ? t('preview.managedAgent.stopTitle') : t('preview.stop.title')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isBusy ? (
                <span className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                  <span>
                    {isManagedAgentProject
                      ? t('preview.managedAgent.busyStopDescription')
                      : t('preview.stop.busyDescription')}
                  </span>
                </span>
              ) : (
                isManagedAgentProject
                  ? t('preview.managedAgent.stopDescription')
                  : t('preview.stop.description')
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('preview.stop.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleStopInstance}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isManagedAgentProject
                ? isBusy ? t('preview.managedAgent.forceStopButton') : t('preview.managedAgent.stopButton')
                : isBusy ? t('preview.stop.forceStop') : t('preview.stop.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
